/**
 * MenuRow — a full-width, borderless text row in a menu card: the settings
 * menu's Feedback / Install guide / Sign out entries, and the project
 * picker's project list.
 *
 *   size     md (14px semibold, the settings menu) or lg (16px bold, the
 *            project list)
 *   divider  the hairline above every row but the first
 *   chevron  a trailing ›, pushed to the far edge (rows that open something)
 *   leading  something before the label, e.g. the selected project's dot
 *   muted    muted text, for an option that isn't the current one
 *
 * Styles are inline on purpose: they must beat stylesheet rules that target
 * buttons in these containers (mark-skin.css fills the selected project row
 * amber otherwise).
 */
import type { ComponentChildren, JSX } from "preact";

export interface MenuRowProps extends Omit<JSX.HTMLAttributes<HTMLButtonElement>, "type" | "size" | "style"> {
  size?: "md" | "lg";
  divider?: boolean;
  chevron?: boolean;
  leading?: ComponentChildren;
  muted?: boolean;
  style?: JSX.CSSProperties;
}

export function MenuRow({
  size = "md",
  divider = false,
  chevron = false,
  leading,
  muted = false,
  style,
  children,
  ...rest
}: MenuRowProps) {
  return (
    <button
      type="button"
      {...rest}
      style={{
        display: "flex",
        alignItems: "center",
        ...(chevron ? { justifyContent: "space-between" } : {}),
        ...(leading ? { gap: "12px" } : {}),
        width: "100%",
        background: "none",
        border: "none",
        ...(divider ? { borderTop: "1px solid var(--mark-bg-surface-alt)" } : {}),
        boxShadow: "none",
        borderRadius: 0,
        padding: "14px 0",
        fontFamily: "inherit",
        fontSize: size === "lg" ? "16px" : "14px",
        fontWeight: size === "lg" ? 700 : 600,
        color: muted ? "var(--mark-text-muted)" : "var(--mark-text-primary)",
        cursor: "pointer",
        textAlign: "left",
        ...style,
      }}
    >
      {leading}
      {children}
      {chevron && <span style={{ color: "var(--mark-text-faint)" }}>{"›"}</span>}
    </button>
  );
}
